/**
 * EL HISTORIAL DE PARTIDOS DE UNA PERSONA.
 *
 * Todos los que jugó, de todos los clubes y de todos los años, del más nuevo
 * al más viejo. Hasta ahora un resultado se podía mirar UNA vez —en el cuadro
 * del torneo, mientras el torneo estaba abierto— y después había que
 * acordarse de en qué torneo fue para volver a encontrarlo.
 *
 * SE LEE DESDE EL LADO DE ESA PERSONA. El servidor ya manda el marcador con
 * su número primero y un `won` que dice cómo terminó: en una llave, estar
 * arriba o abajo es un detalle del dibujo, y en un historial lo único que
 * importa es si ganó y contra quién.
 *
 * LOS FILTROS TRABAJAN SOBRE LO QUE YA ESTÁ. Alguien que juega cincuenta
 * partidos por año durante diez años tiene quinientas filas: una consulta, no
 * un problema. Pedirle al servidor cada vez que alguien toca un desplegable
 * sería un viaje por una cuenta que se hace sola.
 *
 * ES UN COMPONENTE Y NO UNA PANTALLA porque va en dos lados: el perfil propio
 * y el de cualquier otra persona. La misma tabla, los mismos filtros.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Paginador, usePaginado } from './Paginado';
import { Link } from 'react-router-dom';
import { formatScore, type PlayedMatch } from '@tennisup/contracts';

import { Alert } from './Alert';
import { Select } from './Select';
import { api } from '../lib/api';
import { rutaDelPartido } from '../lib/activityActions';
import { errorMessage } from '../lib/errorMessage';
import { monthYear, yearOf } from '../lib/monthYear';

/** El valor de «sin filtrar». Vacío no sirve: un club podría llamarse así. */
const TODOS = '__todos__';

export function MatchHistory({ userId }: { userId: string }): JSX.Element {
  const { t, i18n } = useTranslation();
  const [filas, setFilas] = useState<PlayedMatch[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [año, setAño] = useState(TODOS);
  const [club, setClub] = useState(TODOS);

  const load = useCallback(async () => {
    try {
      setFilas(await api.playerMatches(userId));
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [userId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * SI LO QUE LLEGÓ NO ES UNA LISTA, SE TRATA COMO VACÍA.
   *
   * El tipo dice `PlayedMatch[]`, pero un tipo es una promesa sobre el
   * contrato y no una garantía sobre lo que viaja por la red. Esto vive
   * ADENTRO del perfil: con un `.map` sobre algo que no es una lista, un
   * historial raro no deja una tabla vacía, deja la pantalla del perfil
   * entera en blanco —la foto, los datos y la contraseña incluidos—. Ya pasó
   * dos veces en esta aplicación con datos que el tipo prometía.
   */
  const todas = useMemo(() => (Array.isArray(filas) ? filas : []), [filas]);

  /**
   * LOS AÑOS Y LOS CLUBES SALEN DE LO QUE HAY, no de una lista fija: ofrecer
   * un año sin un solo partido es ofrecer un filtro que solo puede vaciar la
   * lista, y quien lo toca cree que se rompió algo.
   */
  const años = useMemo(
    () =>
      [...new Set(todas.map((f) => yearOf(f.playedOn)).filter((a): a is number => a !== null))].sort(
        (a, b) => b - a,
      ),
    [todas],
  );
  /*
   * UN AMISTOSO NO TIENE CLUB, así que no puede aparecer en el desplegable de
   * clubes. Se filtran antes de armar la lista: una opción sin nombre es una
   * opción que nadie sabe qué elige.
   */
  const clubes = useMemo(() => {
    const porId = new Map(
      todas
        .filter((f): f is typeof f & { communityId: string; communityName: string } =>
          f.communityId !== null && f.communityName !== null,
        )
        .map((f) => [f.communityId, f.communityName] as const),
    );
    return [...porId].sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [todas]);

  const visibles = todas.filter(
    (fila) =>
      (año === TODOS || String(yearOf(fila.playedOn)) === año) &&
      (club === TODOS || fila.communityId === club),
  );

  /**
   * DE A DIEZ. `reiniciarCon` son los dos filtros: cambiar de año o de club
   * trae OTRA lista, y quedarse en la página 3 de la anterior se lee como que
   * el filtro no encontró nada.
   */
  const pagina = usePaginado(visibles, `${año}|${club}`);

  /* GANADOS Y PERDIDOS DE LO QUE SE ESTÁ MIRANDO, no del total. Filtrar por
     un año y seguir viendo el número de siempre haría dudar de cuál de los
     dos está mal. */
  const ganados = visibles.filter((f) => f.outcome === 'WON').length;

  return (
    <section>
      <h2>{t('matchHistory.title')}</h2>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/* Un desplegable con una sola opción no filtra nada: es un control que
          se ve y no hace, y hace dudar de si está roto. */}
      {años.length > 1 && (
        <div className="field">
          <label htmlFor="mh-year">{t('history.year')}</label>
          <Select
            id="mh-year"
            value={año}
            options={[
              { value: TODOS, label: t('history.allYears') },
              ...años.map((a) => ({ value: String(a), label: String(a) })),
            ]}
            onChange={setAño}
          />
        </div>
      )}

      {clubes.length > 1 && (
        <div className="field">
          <label htmlFor="mh-club">{t('history.club')}</label>
          <Select
            id="mh-club"
            value={club}
            options={[
              { value: TODOS, label: t('history.allClubs') },
              ...clubes.map(([id, nombre]) => ({ value: id, label: nombre })),
            ]}
            onChange={setClub}
          />
        </div>
      )}

      {filas === null ? (
        error === null ? <p className="help">{t('common.loading')}</p> : null
      ) : visibles.length === 0 ? (
        <p className="help">
          {todas.length === 0 ? t('matchHistory.empty') : t('history.noneFound')}
        </p>
      ) : (
        <>
          <p className="help" role="status">
            {t('matchHistory.record', {
              won: ganados,
              lost: visibles.length - ganados,
              count: visibles.length,
            })}
          </p>

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('matchHistory.opponent')}</th>
                  <th scope="col">{t('matchHistory.result')}</th>
                  <th scope="col">{t('history.when')}</th>
                </tr>
              </thead>
              <tbody>
                {pagina.visibles.map((fila) => (
                  <tr key={fila.matchId}>
                    <td>
                      <Link to={`/jugadores/${fila.opponentUserId}`}>{fila.opponentName}</Link>
                      {/* EL TORNEO Y EL FORMATO DEBAJO DEL RIVAL. Un 6-0 en
                          una final no es un 6-0 en la primera ronda de un
                          todos contra todos, y sin eso las dos filas se leen
                          igual.

                          EL NOMBRE CORTO DEL FORMATO Y NO EL LARGO: «Llaves»
                          y no «Llaves: quien pierde queda fuera». El largo
                          existe para ELEGIR el formato, donde hace falta que
                          explique; acá solo nombra algo que ya pasó, y
                          puesto entero se comía tres renglones por fila. */}
                      {/* DÓNDE FUE, y no siempre hay torneo: un reto no se
                          juega dentro de ninguno, y un amistoso tampoco tiene
                          categoría. Se arma con lo que hay en vez de dejar
                          huecos con puntos sueltos entre medio. */}
                      <span className="history-club">
                        {[
                          fila.tournamentName ??
                            t(
                              fila.kind === 'CHALLENGE_RANKING'
                                ? 'matchHistory.kindRanking'
                                : 'matchHistory.kindFriendly',
                            ),
                          fila.level === null ? null : t(`categoryLevel.${fila.level}`),
                          fila.format === null ? null : t(`format.short.${fila.format}`),
                        ]
                          .filter((parte) => parte !== null)
                          .join(' · ')}
                      </span>
                    </td>
                    <td>
                      {/* TRES FINALES. Un amistoso puede quedar empatado, y
                          con el ternario de antes se leía como derrota. */}
                      <span className={fila.outcome === 'WON' ? 'tag tag-ok' : 'tag'}>
                        {t(`matchHistory.${fila.outcome === 'WON' ? 'won' : fila.outcome === 'DRAW' ? 'drew' : 'lost'}`)}
                      </span>{' '}
                      {/* SIN SETS SE DICE POR QUÉ. Una celda vacía al lado de
                          «Ganó» se lee como que falta cargar el marcador, y
                          no como que no se jugó ninguno. */}
                      {/*
                        EL MARCADOR ABRE EL PARTIDO: la foto, los comentarios
                        y las reacciones. Es lo que alguien toca cuando está
                        mirando un resultado y se pregunta «¿cómo fue?», y era
                        justo lo único de esta fila que no llevaba a ningún
                        lado. El nombre del rival sigue abriendo su ficha: son
                        dos preguntas distintas y por eso son dos enlaces.

                        EL NOMBRE ACCESIBLE DICE CONTRA QUIÉN. Diez filas con
                        un enlace que se llama «6-3, 6-4» son diez enlaces que
                        quien usa lector de pantalla no puede distinguir.
                      */}
                      <Link
                        className="history-open"
                        to={rutaDelPartido(fila.kind, fila.matchId)}
                        aria-label={t('matchHistory.open', { name: fila.opponentName })}
                      >
                        {fila.walkover ? (
                          <span className="help">{t('score.wo')}</span>
                        ) : (
                          <span className="history-score">{formatScore(fila.score)}</span>
                        )}
                      </Link>
                    </td>
                    <td>
                      {monthYear(fila.playedOn, i18n.language)}
                      <span className="history-club">{fila.communityName}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />
        </>
      )}
    </section>
  );
}
